import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { CONTROL_TYPES, CONTROL_TYPE_META, OPERATING_STATUS, EFFECTIVENESS, ControlTypeBadge } from "../lib/controlTypes";

const FREQ = ["Continuous", "Daily", "Weekly", "Monthly", "Quarterly", "Semi-Annual", "Annual", "As needed"];
const empty = {
  name: "", objective: "", owner: "", frequency: "Annual", trigger: "", method: "",
  related_systems: "", testing_notes: "", control_type: "preventative",
  operating_status: "operating", last_tested: "", effectiveness: "not_tested",
  linked_requirements: [], linked_documents: [],
};

export default function Controls() {
  const [controls, setControls] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const { canEdit } = useAuth();

  const load = () => api.get("/controls").then((r) => setControls(r.data));
  useEffect(() => { load(); api.get("/requirements").then((r) => setReqs(r.data)); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...c, last_tested: c.last_tested || "" }); setEditId(c.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = { ...form, last_tested: form.last_tested || null };
    try {
      if (editId) await api.put(`/controls/${editId}`, payload);
      else await api.post("/controls", payload);
      setOpen(false); load(); toast.success("Internal control saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this control?")) return; await api.delete(`/controls/${id}`); load(); };
  const toggleReq = (rid) => setForm((f) => ({ ...f, linked_requirements: f.linked_requirements.includes(rid) ? f.linked_requirements.filter((x) => x !== rid) : [...f.linked_requirements, rid] }));
  const reqLabel = (rid) => { const r = reqs.find((x) => x.id === rid); return r ? `${r.standard_id} ${r.req_number}` : "?"; };

  const counts = CONTROL_TYPES.reduce((a, t) => ({ ...a, [t]: controls.filter((c) => (c.control_type || "preventative") === t).length }), {});
  const shown = typeFilter === "all" ? controls : controls.filter((c) => (c.control_type || "preventative") === typeFilter);

  return (
    <div>
      <PageHeader title="Internal Controls" subtitle="Preventative, detective, corrective & directive controls mapped to NERC requirements"
        action={canEdit && <Button data-testid="new-control-btn" onClick={openNew} className="rounded-none bg-primary hover:bg-primary/90"><Plus size={16} className="mr-1" />New Internal Control</Button>} />
      <div className="p-8">
        <div className="flex flex-wrap gap-2 mb-5" data-testid="control-type-filters">
          <button data-testid="control-filter-all" onClick={() => setTypeFilter("all")} className={`border px-3 py-1.5 text-xs font-semibold rounded-none ${typeFilter === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>All ({controls.length})</button>
          {CONTROL_TYPES.map((t) => (
            <button key={t} data-testid={`control-filter-${t}`} onClick={() => setTypeFilter(t)} className={`border px-3 py-1.5 text-xs font-semibold rounded-none capitalize ${typeFilter === t ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>{CONTROL_TYPE_META[t].label} ({counts[t]})</button>
          ))}
        </div>
        {shown.length === 0 ? <Empty title="No internal controls yet" hint="Define a control and map it to requirements, or use “Recommend controls” on the Risk Matrix." /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {shown.map((c) => (
              <div key={c.id} data-testid={`control-card-${c.id}`} className="border border-border bg-card p-5">
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <div className="font-display font-bold text-lg">{c.name}</div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      <ControlTypeBadge type={c.control_type || "preventative"} />
                      <span className="text-[11px] border border-border px-1.5 py-0.5 capitalize text-muted-foreground" data-testid={`control-opstatus-${c.id}`}>{OPERATING_STATUS[c.operating_status || "operating"]}</span>
                    </div>
                  </div>
                  {canEdit && <div className="flex gap-2 shrink-0">
                    <button data-testid={`edit-control-${c.id}`} onClick={() => openEdit(c)} className="text-muted-foreground hover:text-foreground"><PencilSimple size={16} /></button>
                    <button onClick={() => remove(c.id)} className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>
                  </div>}
                </div>
                <p className="text-sm text-muted-foreground mt-2">{c.objective}</p>
                <div className="grid grid-cols-3 gap-x-4 gap-y-1 mt-3 text-xs">
                  <div><span className="overline text-muted-foreground">Owner</span><div>{c.owner || "—"}</div></div>
                  <div><span className="overline text-muted-foreground">Frequency</span><div>{c.frequency}</div></div>
                  <div><span className="overline text-muted-foreground">Effectiveness</span><div className="capitalize">{EFFECTIVENESS[c.effectiveness || "not_tested"]}</div></div>
                </div>
                {c.linked_requirements?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {c.linked_requirements.map((rid) => <span key={rid} className="font-mono text-[11px] bg-primary text-primary-foreground px-1.5 py-0.5">{reqLabel(rid)}</span>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "New"} Internal Control</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="control-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Control Type</Label>
                <Select value={form.control_type} onValueChange={(v) => setForm({ ...form, control_type: v })}>
                  <SelectTrigger data-testid="control-type-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONTROL_TYPES.map((t) => <SelectItem key={t} value={t}>{CONTROL_TYPE_META[t].label}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">{CONTROL_TYPE_META[form.control_type].help}</p>
              </div>
              <div><Label>Operating Status</Label>
                <Select value={form.operating_status} onValueChange={(v) => setForm({ ...form, operating_status: v })}>
                  <SelectTrigger data-testid="control-opstatus-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(OPERATING_STATUS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Objective</Label><Textarea value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} className="rounded-none mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Owner</Label><Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Frequency</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQ.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Effectiveness</Label>
                <Select value={form.effectiveness} onValueChange={(v) => setForm({ ...form, effectiveness: v })}>
                  <SelectTrigger data-testid="control-effectiveness-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(EFFECTIVENESS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Last Tested</Label><Input type="date" value={form.last_tested || ""} onChange={(e) => setForm({ ...form, last_tested: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Trigger</Label><Input value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Related Systems</Label><Input value={form.related_systems} onChange={(e) => setForm({ ...form, related_systems: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <div><Label>Method</Label><Textarea value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>Testing / Review Notes</Label><Textarea value={form.testing_notes} onChange={(e) => setForm({ ...form, testing_notes: e.target.value })} className="rounded-none mt-1" /></div>
            <div>
              <Label>Map to Requirements</Label>
              <div className="border border-border mt-1 max-h-40 overflow-y-auto p-3 space-y-2">
                {reqs.map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox data-testid={`control-link-req-${r.req_number}`} checked={form.linked_requirements.includes(r.id)} onCheckedChange={() => toggleReq(r.id)} className="rounded-none" />
                    <span className="font-mono font-bold">{r.standard_id} {r.req_number}</span> <span className="text-muted-foreground truncate">{r.text.slice(0, 50)}…</span>
                  </label>
                ))}
              </div>
            </div>
            <Button data-testid="save-control-btn" onClick={save} className="rounded-none w-full bg-primary hover:bg-primary/90">Save Internal Control</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
